import type { SignalData } from '../types/gateway';
import { useTranslation } from '../i18n/useTranslation';
import { PanelState } from './PanelState';
import { usePanelState } from '../hooks/usePanelState';
import { PANEL_SOURCE } from '../lib/panelSources';

interface SignalFeedProps {
  signals: SignalData[];
  className?: string;
}

function formatTime(ts: number): string {
  const d = new Date(ts * 1000);
  return d.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function formatPrice(p: number): string {
  return p.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function SignalFeed({ signals, className = '' }: SignalFeedProps) {
  const { t } = useTranslation();
  const signalsState = usePanelState('signals', PANEL_SOURCE.signals);
  return (
    <div className={`bg-card rounded border border-border flex flex-col ${className}`}>
      <div className="drag-handle h-6 flex items-center px-2 cursor-grab active:cursor-grabbing border-b border-border-subtle shrink-0">
        <span className="panel-header">{t('signals.header')}</span>
        <span className="ml-auto text-[8px] font-mono text-muted">{signals.length}</span>
      </div>

      <div className="flex-1 overflow-y-auto min-h-0">
        {signals.length === 0 && (
          <PanelState state={signalsState === 'ready' ? 'live-empty' : signalsState} panelKey="signals" />
        )}
        {signals.map((s, i) => (
          <div
            key={s.id}
            className="flex items-center gap-1.5 px-2 py-1 border-b border-border-subtle/50 animate-fade-up"
            style={{ animationDelay: `${i * 0.02}s` }}
          >
            <span
              className={`text-[9px] font-mono font-bold w-7 ${
                s.side === 'BUY' ? 'text-success' : 'text-danger'
              }`}
            >
              {s.side}
            </span>

            <span className="text-[9px] font-mono text-muted-foreground w-14">
              {formatTime(s.time)}
            </span>

            <span className="text-[9px] font-mono text-foreground flex-1">
              {formatPrice(s.price)}
            </span>

            <span className="text-[9px] font-mono text-muted w-8 text-right">
              {s.qty}
            </span>

            <span
              className={`text-[9px] font-mono w-14 text-right ${
                s.pnl >= 0 ? 'text-success' : 'text-danger'
              }`}
            >
              {s.pnl >= 0 ? '+' : ''}{s.pnl.toFixed(0)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
